import { createContext, useContext, type ReactNode } from 'react';
import type { GhanttHorizontalScroll } from './ghanttHorizontalScroll';
import type { GhanttTimelineSlot, GhanttUpperSegment } from './ganttTimeline';
import type { GhanttAppearanceProps, GhanttEventsProps, GhanttTimelineProps } from './types';

/** Eje temporal diario resuelto (misma ventana para cabecera, celdas y % de barras). `endExclusiveMs` es exclusivo. */
export interface GhanttDayAxis {
  startMs: number;
  endExclusiveMs: number;
}

/** Valor del contexto: timeline y opciones compartidas por la columna Gantt (cabecera + celdas). */
export interface GhanttConfigContextValue<T = unknown> {
  timeline: GhanttTimelineProps;
  appearance: GhanttAppearanceProps;
  events: GhanttEventsProps<T>;
  /**
   * Slots del timeline precalculados por {@link Ghantt} (misma referencia para cabecera y todas las filas).
   * Evita repetir `ghanttBuildTimelineSlots` en cada celda cuando el rango es muy largo.
   */
  timelineSlots?: readonly GhanttTimelineSlot[];
  /** Segmentos de la planta superior (meses/años) precalculados. */
  upperSegments?: readonly GhanttUpperSegment[];
  /** Presente con `scale: 'day'`: rango ampliado a meses completos para scroll y alineación de barras. */
  dayAxis?: GhanttDayAxis | null;
  /** Scroll horizontal unificado (cabecera = viewport; cuerpo = translate). Solo lo define {@link Ghantt}. */
  horizontalScroll?: GhanttHorizontalScroll | null;
}

const GhanttConfigContext = createContext<GhanttConfigContextValue<unknown> | null>(null);

export function GhanttConfigProvider<T>({
  value,
  children,
}: {
  value: GhanttConfigContextValue<T>;
  children: ReactNode;
}) {
  return (
    <GhanttConfigContext.Provider value={value as GhanttConfigContextValue<unknown>}>
      {children}
    </GhanttConfigContext.Provider>
  );
}

/**
 * Configuración de la columna Gantt inyectada por el organismo `Ghantt`.
 * Fuera del proveedor devuelve `null`.
 */
export function useGhanttConfig<T = unknown>(): GhanttConfigContextValue<T> | null {
  return useContext(GhanttConfigContext) as GhanttConfigContextValue<T> | null;
}
